import { FullViewImage } from "../../media/FullViewImage";
import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { frameClass, frameFromSize, MediaFrame } from "./mediaFrame";
import {
  isPreviewDoc,
  isPreviewImage,
  isPreviewVideo,
  pauseMediaElement,
  previewMediaUrl,
} from "./previewMedia";

const PreviewStage = ({
  src,
  item,
  imgRef,
  onFrame,
  zoomed,
}: {
  src: string;
  item?: any;
  imgRef: React.RefObject<HTMLImageElement | null>;
  onFrame: (frame: MediaFrame) => void;
  zoomed: boolean;
}) => {
  const [asVideo, setAsVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const userPlayedRef = useRef(false);
  const [videoPaused, setVideoPaused] = useState(true);
  const treatAsVideo = asVideo || isPreviewVideo(item || src);

  useEffect(() => {
    setAsVideo(false);
    userPlayedRef.current = false;
    setVideoPaused(true);
    onFrame("unknown");
    // Reset only when the take changes. onFrame is setState and stays stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (!src) {
    return <p className="hkp-media-zoom__empty">This take has no file.</p>;
  }

  if (isPreviewDoc(item || src)) {
    return (
      <iframe
        title="Preview"
        src={`https://docs.google.com/viewer?url=${encodeURIComponent(src)}&embedded=true`}
        frameBorder="0"
      />
    );
  }

  if (treatAsVideo) {
    return (
      <>
        <video
          key={src}
          ref={videoRef}
          src={src}
          controls
          playsInline
          preload="metadata"
          autoPlay={false}
          onLoadedMetadata={(event) => {
            if (!userPlayedRef.current) event.currentTarget.pause();
            onFrame(frameFromSize(event.currentTarget.videoWidth, event.currentTarget.videoHeight));
          }}
          onPlay={() => {
            userPlayedRef.current = true;
            setVideoPaused(false);
          }}
          onPause={() => setVideoPaused(true)}
          onEnded={() => setVideoPaused(true)}
        />
        {videoPaused ? (
          <button
            type="button"
            className="hkp-media-zoom__play"
            onClick={() => {
              const el = videoRef.current;
              if (!el) return;
              userPlayedRef.current = true;
              el.play().then(() => setVideoPaused(false)).catch(() => undefined);
            }}
            aria-label="Play video"
          >
            <span className="hkp-media-play" aria-hidden="true">
              <span className="hkp-media-play__triangle" />
            </span>
          </button>
        ) : null}
      </>
    );
  }

  return (
    <FullViewImage
      item={item || src}
      src={src}
      zoomed={zoomed}
      ref={imgRef as React.Ref<HTMLImageElement>}
      onLoad={(event) => onFrame(frameFromSize(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight))}
      onError={() => setAsVideo(true)}
    />
  );
};

const ProductImagePreView = (props: any) => {
  const { openModal, closeModal, images }: any = props;
  const slides = Array.isArray(images) ? images.filter((item: any) => previewMediaUrl(item)) : [];
  const [index, setIndex] = useState(0);
  const [frame, setFrame] = useState<MediaFrame>("unknown");
  const [zoomed, setZoomed] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const current = slides[index];
  const src = previewMediaUrl(current);
  const canStep = slides.length > 1;

  useEffect(() => {
    if (openModal) {
      setIndex(0);
      setZoomed(false);
      setFrame("unknown");
    } else {
      document
        .querySelectorAll<HTMLVideoElement>(".hkp-media-viewer video")
        .forEach((el) => pauseMediaElement(el));
    }
  }, [openModal]);

  const step = (delta: number) => {
    if (!canStep) return;
    document
      .querySelectorAll<HTMLVideoElement>(".hkp-media-viewer video")
      .forEach((el) => pauseMediaElement(el));
    setFrame("unknown");
    setZoomed(false);
    setIndex((currentIndex) => (currentIndex + delta + slides.length) % slides.length);
  };

  const zoomIn = () => {
    const pic = imgRef.current;
    if (!pic) return;
    setZoomed(true);
    pic.style.width = pic.clientWidth + 100 + "px";
    pic.style.height = pic.clientHeight + 50 + "px";
  };
  const zoomOut = () => {
    const pic = imgRef.current;
    if (!pic) return;
    pic.style.width = Math.max(120, pic.clientWidth - 50) + "px";
    pic.style.height = Math.max(80, pic.clientHeight - 50) + "px";
  };

  const hasImage = current ? isPreviewImage(current) && !isPreviewVideo(current) : false;
  const geo =
    current && typeof current === "object" && current.lat != null && current.lng != null
      ? `${Number(current.lat).toFixed(4)}, ${Number(current.lng).toFixed(4)}`
      : "";

  return (
    <div className="ModalDiaLOgue">
      <Modal
        isOpen={openModal}
        toggle={closeModal}
        fullscreen
        className="hkp-media-viewer"
        modalClassName="hkp-media-viewer"
      >
        <ModalHeader>
          {hasImage ? (
            <div className="ZoomDiv">
              <svg
                onClick={() => zoomIn()}
                width="25"
                height="25"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.77779 12.5556C9.96877 12.5556 12.5556 9.96877 12.5556 6.77779C12.5556 3.5868 9.96877 1 6.77779 1C3.5868 1 1 3.5868 1 6.77779C1 9.96877 3.5868 12.5556 6.77779 12.5556Z"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14.0001 13.9996L10.8584 10.8579"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.77734 4.61096V8.9443"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.61133 6.7782H8.94467"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span></span>
              <svg
                onClick={() => zoomOut()}
                width="25"
                height="25"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.77779 12.5556C9.96877 12.5556 12.5556 9.96877 12.5556 6.77779C12.5556 3.5868 9.96877 1 6.77779 1C3.5868 1 1 3.5868 1 6.77779C1 9.96877 3.5868 12.5556 6.77779 12.5556Z"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14.0001 13.9996L10.8584 10.8579"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.61133 6.7782H8.94467"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          ) : (
            <div></div>
          )}

          <div className="Heading">
            <svg
              onClick={() => closeModal()}
              width="34"
              height="34"
              viewBox="0 0 34 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="34" height="34" rx="4" fill="#262D22" />
              <path
                d="M22 12L12 22"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 12L22 22"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className={`hkp-media-zoom ${frameClass(frame)}`}>
            {canStep ? (
              <button type="button" className="hkp-media-zoom__nav is-prev" onClick={() => step(-1)} aria-label="Previous">
                ‹
              </button>
            ) : null}
            <div className="hkp-media-zoom__stage">
              {current ? (
                <PreviewStage key={src} src={src} item={current} imgRef={imgRef} onFrame={setFrame} zoomed={zoomed} />
              ) : null}
            </div>
            {canStep ? (
              <button type="button" className="hkp-media-zoom__nav is-next" onClick={() => step(1)} aria-label="Next">
                ›
              </button>
            ) : null}
          </div>
          <div className="hkp-media-zoom__dock">
            {slides.length ? (
              <p className="hkp-media-zoom__count">
                {index + 1} of {slides.length}
              </p>
            ) : null}
            {geo ? <p className="hkp-media-zoom__geo">{geo}</p> : null}
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ProductImagePreView;

